// beforeSend filter for the web app's Sentry client: drops states the UI handles (401/403, cancellations, aborts),
// frameless "Script error.", events with no app /assets/ frame, extension JSON-RPC / EIP-1193 / Google-status object
// rejections, page-dispatched rejection events (`isTrusted: false`) and browser-injected Etsy image fetch failures.
import type { ErrorEvent } from '@sentry/browser'

const AXIOS_STATUS = /status code (\d{3})/
const HTTP_UNAUTHORIZED = 401
const HTTP_FORBIDDEN = 403
const EXPECTED_AXIOS_STATUSES = new Set([HTTP_UNAUTHORIZED, HTTP_FORBIDDEN])
const AXIOS_ABORT_MESSAGES = new Set(['canceled', 'Request aborted'])
const APP_SCRIPT_PATH = '/assets/'
const ABORT_ERROR = 'AbortError'
const OBJECT_REJECTION_TYPE = 'UnhandledRejection'
const JSON_RPC_RESERVED_MIN = -32768
const JSON_RPC_RESERVED_MAX = -32000
const EIP1193_USER_REJECTED = 4001
const EIP1193_UNAUTHORIZED = 4100
const EIP1193_UNSUPPORTED_METHOD = 4200
const EIP1193_DISCONNECTED = 4900
const EIP1193_CHAIN_DISCONNECTED = 4901
const EIP1193_PROVIDER_CODES = new Set([
  EIP1193_USER_REJECTED,
  EIP1193_UNAUTHORIZED,
  EIP1193_UNSUPPORTED_METHOD,
  EIP1193_DISCONNECTED,
  EIP1193_CHAIN_DISCONNECTED
])
const GOOGLE_API_STATUS = /^[A-Z]+(?:_[A-Z]+)*$/
const ETSY_IMAGE_FETCH_FAILURE = /^(Load failed|Failed to fetch) \(i\.etsystatic\.com\)$/

type StackFrame = { filename?: string }

type ExceptionValue = {
  type?: string
  value?: string
  stacktrace?: { frames?: StackFrame[] }
  mechanism?: { handled?: boolean }
}

const firstException = (event: ErrorEvent): ExceptionValue | undefined => event.exception?.values?.[0]

const isAppScript = (filename: string | undefined, origin: string): boolean =>
  (filename ?? '').startsWith(`${origin}${APP_SCRIPT_PATH}`)

const hasNoAppFrame = (frames: StackFrame[] | undefined, origin: string): boolean =>
  !!frames?.length && !frames.some((frame) => isAppScript(frame.filename, origin))

const isFramelessScriptError = (value: string | undefined, frames: StackFrame[] | undefined): boolean =>
  value === 'Script error.' && !frames?.length

const isUnhandled = (exception: ExceptionValue): boolean => exception.mechanism?.handled === false

const isUnhandledAbort = (exception: ExceptionValue): boolean =>
  isUnhandled(exception) && (exception.type === ABORT_ERROR || (exception.value ?? '').startsWith(`${ABORT_ERROR}:`))

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const isJsonRpcCode = (code: number): boolean =>
  (code >= JSON_RPC_RESERVED_MIN && code <= JSON_RPC_RESERVED_MAX) || EIP1193_PROVIDER_CODES.has(code)

const isExtensionObjectRejection = (event: ErrorEvent, exception: ExceptionValue): boolean => {
  if (!isUnhandled(exception) || exception.type !== OBJECT_REJECTION_TYPE) return false
  const payload = event.extra?.__serialized__
  if (!isRecord(payload) || typeof payload.code !== 'number' || typeof payload.message !== 'string') return false
  return isJsonRpcCode(payload.code) || (typeof payload.status === 'string' && GOOGLE_API_STATUS.test(payload.status))
}

const isScriptDispatchedEventRejection = (event: ErrorEvent, exception: ExceptionValue): boolean => {
  const payload = event.extra?.__serialized__
  return isUnhandled(exception) && isRecord(payload) && payload.isTrusted === false
}

const isInjectedEtsyImageFetchFailure = (exception: ExceptionValue): boolean =>
  isUnhandled(exception) && exception.type === 'TypeError' && ETSY_IMAGE_FETCH_FAILURE.test(exception.value ?? '')

const isUnhandledNoise = (event: ErrorEvent, exception: ExceptionValue): boolean =>
  isUnhandledAbort(exception) ||
  isExtensionObjectRejection(event, exception) ||
  isScriptDispatchedEventRejection(event, exception) ||
  isInjectedEtsyImageFetchFailure(exception)

const isExpectedAxiosFailure = (event: ErrorEvent, exception: ExceptionValue): boolean => {
  if (exception.type !== 'AxiosError') return false
  if (AXIOS_ABORT_MESSAGES.has(exception.value ?? '')) return true
  const status = axiosStatusFrom(event)
  return status !== undefined && EXPECTED_AXIOS_STATUSES.has(status)
}

export const axiosStatusFrom = (event: ErrorEvent): number | undefined => {
  const exception = firstException(event)
  if (exception?.type !== 'AxiosError') return undefined

  const match = AXIOS_STATUS.exec(exception.value ?? '')
  return match ? Number(match[1]) : undefined
}

/** `origin` is the page's `window.location.origin`, the only origin the app's own scripts load from. */
export const shouldReportEvent = (event: ErrorEvent, origin: string): boolean => {
  const exception = firstException(event)
  if (!exception) return true
  if (isExpectedAxiosFailure(event, exception)) return false

  const frames = event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? [])
  if (isFramelessScriptError(exception.value, frames)) return false
  if (hasNoAppFrame(frames, origin)) return false
  if (isUnhandledNoise(event, exception)) return false

  return true
}
